import { idsByPhrasing, phrasingKey, type MatchableEntry, type MatchThresholds, type QuestionMatcher, type RankedEntry, type RankRequest } from "./questionMatcher";

/** Turns texts into unit-length vectors, so a dot product is their cosine similarity. */
export type Embed = (texts: string[]) => Promise<number[][]>;

/**
 * Cosine similarities from the sentence-embedding model. Starting values come from
 * asking each catalogue example phrasing against all the others: 59 of 90 were
 * answered right, 2 wrong and 21 got "Did you mean…". Off-topic messages scored
 * below 0.3. Retune after catalogue changes.
 */
export const EMBEDDING_THRESHOLDS: MatchThresholds = {
  answer: 0.65,
  clearLead: 0.08,
  didYouMean: 0.5,
  related: 0.35,
};

const dot = (a: number[], b: number[]) =>
  a.reduce((sum, x, i) => sum + x * b[i], 0);

/** How many example phrasings are embedded at once while the model gets ready. */
const WARMUP_BATCH = 24;

/** One huge batch is cut off by the embed timeout, so the sparkle never appears. */
async function embedInBatches(embed: Embed, texts: string[]): Promise<number[][]> {
  const vectors: number[][] = [];
  for (let start = 0; start < texts.length; start += WARMUP_BATCH) {
    vectors.push(...(await embed(texts.slice(start, start + WARMUP_BATCH))));
  }
  return vectors;
}

/**
 * Scores each entry by its closest example phrasing. The model loads once, when
 * the matcher is created, and the example phrasings are embedded straight after.
 */
export function createEmbeddingMatcher(
  entries: MatchableEntry[],
  loadEmbed: () => Promise<Embed>
): QuestionMatcher {
  const loading = loadEmbed().then(async (embed) => {
    const vectors = await embedInBatches(embed, entries.flatMap((e) => e.examples));
    let next = 0;
    const examplesById = new Map(
      entries.map((e) => [e.id, e.examples.map(() => vectors[next++])])
    );
    return { embed, examplesById };
  });

  const phrasings = idsByPhrasing(entries);

  const scoreAgainst = (
    query: number[],
    text: string,
    allowedIds: string[],
    examplesById: Map<string, number[][]>
  ): RankedEntry[] => {
    const exactIds = phrasings.get(phrasingKey(text));
    return allowedIds
      .map((id) => ({
        id,
        score: Math.max(
          -1,
          ...(examplesById.get(id) ?? []).map((ex) => dot(query, ex))
        ),
        ...(exactIds?.has(id) ? { exact: true as const } : {}),
      }))
      .sort((a, b) => b.score - a.score);
  };

  /**
   * Embeds each distinct text in `requests` once, then scores every pairing from that
   * one batch: two rankings for the same text share its vector instead of asking the
   * model twice. Scoped to this call only, so a failure never lingers for the next one.
   */
  async function rankMany(requests: RankRequest[]): Promise<RankedEntry[][]> {
    const { embed, examplesById } = await loading;
    const distinctTexts = Array.from(new Set(requests.map((r) => r.text)));
    const vectors = await embed(distinctTexts);
    const vectorByText = new Map(distinctTexts.map((text, i) => [text, vectors[i]]));
    return requests.map(({ text, allowedIds }) =>
      scoreAgainst(vectorByText.get(text) as number[], text, allowedIds, examplesById)
    );
  }

  return {
    ready: loading.then(() => undefined),
    thresholds: EMBEDDING_THRESHOLDS,
    rank: async (text, allowedIds) => (await rankMany([{ text, allowedIds }]))[0],
    rankMany,
    scorePhrases: async (text, phrases) => {
      const { embed } = await loading;
      const [query, ...rest] = await embed([text, ...phrases]);
      return rest.map((phrase) => dot(query, phrase));
    },
  };
}
